declare global { interface Window { gtag?: (...args: unknown[]) => void; } }

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, ChevronDown, ChevronLeft, ChevronRight, Search, Check, ArrowRight, Upload } from 'lucide-react';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond/dist/filepond.min.css';

registerPlugin(FilePondPluginFileValidateSize, FilePondPluginFileValidateType);

/* FilePond pre-signed upload config for Web3Forms Advanced File Uploader */
const filepondServer = {
  process: async (
    _fieldName: string,
    file: File,
    _metadata: Record<string, unknown>,
    load: (key: string) => void,
    error: (msg: string) => void,
    progress: (computable: boolean, loaded: number, total: number) => void,
    abort: () => void,
  ) => {
    try {
      const presigned = await fetch(`https://api.web3forms.com/upload?file=${encodeURIComponent(file.name)}`).then(r => r.json());
      if (!presigned.url) { error('Failed to get upload URL'); return; }

      // Web3Forms returns an S3 POST policy — must POST with all fields + file
      const formData = new FormData();
      if (presigned.fields) {
        Object.entries(presigned.fields as Record<string, string>).forEach(([k, v]) => formData.append(k, v));
      }
      formData.append('Content-Type', file.type);
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', presigned.url);
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) progress(true, e.loaded, e.total); };
      xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) load(presigned.key); else error('Upload failed'); };
      xhr.onerror = () => error('Upload failed');
      xhr.send(formData);
      return { abort: () => { xhr.abort(); abort(); } };
    } catch {
      error('Upload failed');
    }
  },
};

const FREE_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com'];
const COMPANY_TYPES = ['Tech / AI Product Company', 'Data Aggregator / Marketplace', 'Agency / Research', 'Other'];
const LANGUAGES = [
  'Hindi', 'English (Indian)', 'Tamil', 'Telugu', 'Bengali', 'Marathi',
  'Gujarati', 'Kannada', 'Malayalam', 'Punjabi', 'Odia', 'Urdu',
  'Assamese', 'Maithili', 'Sanskrit', 'Konkani', 'Dogri', 'Bodo',
  'Santali', 'Kashmiri', 'Sindhi', 'Nepali', 'Manipuri', 'Other',
];
const VOLUMES = ['Under 50 hours (Pilot / Testing)', '50 – 250 hours', '250 – 1,000 hours', '1,000+ hours (Enterprise Scale)'];
const TIMELINES = ['Immediate', 'Within 3 Months', 'Exploring / 6+ Months Out'];
const BUDGETS = ['Under $10,000', '$10,000 – $50,000', '$50,000 – $100,000', '$100,000+'];

const STEPS = [
  { num: '01', title: 'The Basics', desc: 'Tell us who you are' },
  { num: '02', title: 'Your Requirement', desc: 'All fields optional — fill what you can' },
  { num: '03', title: 'Anything Else', desc: 'Final details & attachments' },
];

/* ═══════════════════════════════════════════════════════════
   Unified Project Brief — portal overlay for desktop & mobile
   Slides in from right, step-wise wizard, blurred backdrop
   ═══════════════════════════════════════════════════════════ */
export function ProjectBriefOverlay({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [step, setStep] = useState(0);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [company, setCompany] = useState('');
  const [companyType, setCompanyType] = useState('');
  const [primaryGoal, setPrimaryGoal] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [langOpen, setLangOpen] = useState(false);
  const [langSearch, setLangSearch] = useState('');
  const [volume, setVolume] = useState('');
  const [timeline, setTimeline] = useState('');
  const [budget, setBudget] = useState('');
  const [details, setDetails] = useState('');
  const [pondFiles, setPondFiles] = useState<any[]>([]);
  const [uploadedKeys, setUploadedKeys] = useState<string[]>([]);

  const langRef = useRef<HTMLDivElement>(null);

  // Reset step when opened
  useEffect(() => {
    if (isOpen) { setStep(0); setSubmitted(false); }
  }, [isOpen]);

  // Lock scroll
  useEffect(() => {
    if (isOpen) {
      const sc = document.getElementById('main-scroll-container');
      if (sc) sc.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      return () => {
        if (sc) sc.style.overflow = '';
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Close lang dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const validateEmail = useCallback((val: string) => {
    setEmail(val);
    if (!val) { setEmailError(''); return; }
    // Basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    const domain = val.split('@')[1]?.toLowerCase();
    if (domain && FREE_DOMAINS.includes(domain)) {
      setEmailError('Please use your company email address to proceed.');
    } else { setEmailError(''); }
  }, []);

  const toggleGoal = (goal: string) => {
    setPrimaryGoal(prev => prev.includes(goal) ? prev.filter(g => g !== goal) : [...prev, goal]);
  };

  const toggleLanguage = (lang: string) => {
    setLanguages(prev => prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]);
  };

  const filteredLangs = LANGUAGES.filter(l => l.toLowerCase().includes(langSearch.toLowerCase()));

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (emailError || submitting) return;
    setSubmitting(true);
    try {
      const payload: Record<string, string> = {
        access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
        subject: `New Project Brief from ${name} - ${company}`,
        from_name: 'Voqals Project Brief',
        replyto: email,
        Name: name,
        Email: email,
        Company: company,
        'Company Type': companyType,
        'Primary Goal': primaryGoal.join(', ') || '-',
        Languages: languages.join(', ') || '-',
        Volume: volume || '-',
        Timeline: timeline || '-',
        Budget: budget || '-',
        'Additional Details': details || '-',
      };
      if (uploadedKeys.length > 0) payload.attachment = uploadedKeys.join(',');
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then(r => r.json());
      console.log('Web3Forms response:', res);
      if (res.success) {
        setSubmitted(true);
        window.gtag?.('event', 'conversion', { send_to: 'AW-18066710209/65G5CK-OyJYcEMG98KZD', value: 1.0, currency: 'INR' });
      } else {
        console.error('Web3Forms rejected:', res);
        alert('Submission failed: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Submission error:', err);
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedStep0 = name && email && !emailError && company && companyType;

  const next = () => setStep(s => Math.min(s + 1, 2));
  const prev = () => setStep(s => Math.max(s - 1, 0));

  // Two-phase render: `visible` keeps the DOM mounted during exit animation
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
    } else {
      setAnimateIn(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (visible && isOpen) {
      const id = setTimeout(() => setAnimateIn(true), 20);
      return () => clearTimeout(id);
    }
  }, [visible, isOpen]);

  const handleTransitionEnd = useCallback(() => {
    if (!isOpen) setVisible(false);
  }, [isOpen]);

  if (!visible) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex transition-opacity duration-300"
      style={{ opacity: animateIn ? 1 : 0, pointerEvents: animateIn ? 'auto' : 'none' }}
      onTransitionEnd={(e) => { if (e.propertyName === 'opacity' && !animateIn) handleTransitionEnd(); }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[12px] transition-opacity duration-300"
        style={{ opacity: animateIn ? 1 : 0 }}
        onClick={onClose}
      />

      {/* Panel — slides from right, inset on desktop */}
      <div
        className="relative z-10 ml-auto w-full md:w-[clamp(600px,55vw,960px)] h-full md:h-auto md:my-[clamp(60px,5.5vw,96px)] md:mr-[clamp(16px,1.5vw,28px)] md:rounded-[clamp(20px,1.85vw,32px)] flex flex-col overflow-hidden transition-transform duration-300 ease-out"
        style={{
          background: 'linear-gradient(180deg, rgba(8,8,20,0.97) 0%, rgba(6,6,16,0.98) 100%)',
          transform: animateIn ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
            {/* Gradient border — desktop only */}
            <div className="hidden md:block absolute inset-0 rounded-[inherit] pointer-events-none z-10" style={{
              padding: '1.5px',
              background: 'linear-gradient(135deg, rgba(86,207,252,0.5) 0%, rgba(27,54,255,0.3) 50%, rgba(86,207,252,0.25) 100%)',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              maskComposite: 'exclude',
            }} />

            {/* Subtle left-edge gradient line — mobile */}
            <div className="md:hidden absolute left-0 top-0 bottom-0 w-[1.5px] bg-gradient-to-b from-[#56CFFC]/40 via-[#1B36FF]/30 to-transparent" />

            {/* Decorative orbs */}
            <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[radial-gradient(circle,rgba(86,207,252,0.04)_0%,transparent_70%)] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[250px] h-[250px] bg-[radial-gradient(circle,rgba(27,54,255,0.06)_0%,transparent_70%)] pointer-events-none" />

            {/* ── Header ── */}
            <div className="relative flex items-center justify-between px-5 md:px-[clamp(28px,2.5vw,40px)] pt-5 md:pt-[clamp(20px,2vw,28px)] pb-4 md:pb-[clamp(14px,1.2vw,20px)] border-b border-white/5 shrink-0">
              <button onClick={step === 0 ? onClose : prev} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group">
                <div className="w-8 h-8 md:w-9 md:h-9 rounded-full border border-white/10 group-hover:border-white/20 flex items-center justify-center transition-colors">
                  <ChevronLeft size={16} />
                </div>
                <span className="text-[10px] md:text-[clamp(9px,0.69vw,11px)] font-bold uppercase tracking-widest">
                  {step === 0 ? 'Close' : 'Back'}
                </span>
              </button>

              {/* Step indicator */}
              <div className="flex items-center gap-1.5">
                {STEPS.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { if (i === 0 || canProceedStep0) setStep(i); }}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === step ? 'w-6 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF]' : i < step ? 'w-3 bg-[#56CFFC]/40' : 'w-3 bg-white/10'
                    }`}
                  />
                ))}
              </div>

              <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 transition-colors text-gray-500 hover:text-white border border-white/5">
                <X size={14} />
              </button>
            </div>

            {submitted ? (
              /* ── Success Screen ── */
              <div className="flex-1 flex flex-col items-center justify-center px-8 py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center mb-6">
                  <Check size={28} className="text-[#56CFFC]" />
                </div>
                <h2 className="text-white font-black text-[clamp(22px,2vw,36px)] tracking-tight uppercase mb-3">
                  Brief Received
                </h2>
                <p className="text-white/80 text-[clamp(12px,0.9vw,15px)] leading-relaxed max-w-sm mb-2">
                  Thank you, {name.split(' ')[0]}. Our team will review your project brief and reach out within 24 hours.
                </p>
                <p className="text-white/60 text-[clamp(10px,0.75vw,12px)] mb-8">
                  A copy has been sent to <span className="text-white/80">{email}</span>
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-8 py-3 rounded-full border border-white/10 text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white/5 transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
            <>
            {/* ── General form title ── */}
            <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] pt-5 md:pt-6 pb-0 shrink-0">
              <h1 className="text-white font-black text-[clamp(16px,1.4vw,24px)] tracking-tight uppercase">Start Your Project</h1>
            </div>

            {/* ── Step title bar ── */}
            <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] pt-3 md:pt-4 pb-2 shrink-0">
              <div className="flex items-baseline gap-3">
                <span className="text-[clamp(9px,0.69vw,11px)] font-bold tracking-[0.2em] uppercase text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF]">
                  Step {STEPS[step].num}
                </span>
                <span className="text-gray-600 text-[9px] md:text-[10px]">of 03</span>
              </div>
              <h2 className="text-white font-black text-[clamp(20px,1.8vw,32px)] tracking-tight uppercase mt-1">
                {STEPS[step].title}
              </h2>
              <p className="text-gray-500 text-[clamp(10px,0.8vw,13px)] tracking-wide mt-0.5">{STEPS[step].desc}</p>
            </div>

            {/* ── Form body ── */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 md:px-[clamp(28px,2.5vw,40px)] pt-4 md:pt-5 pb-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* ── STEP 1: The Basics (all required) ── */}
                  {step === 0 && (
                    <div className="flex flex-col gap-5 md:gap-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                        <Field label="Name">
                          <Input value={name} onChange={setName} placeholder="Jane Doe" required />
                        </Field>
                        <Field label="Work Email">
                          <Input value={email} onChange={validateEmail} placeholder="jane@company.com" type="email" error={!!emailError} required />
                          {emailError && <p className="text-red-400 text-[10px] mt-1">{emailError}</p>}
                        </Field>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Company Name">
                          <Input value={company} onChange={setCompany} placeholder="Acme AI" required />
                        </Field>
                        <Field label="Company Type">
                          <StyledSelect value={companyType} onChange={setCompanyType} options={COMPANY_TYPES} placeholder="Select type" required />
                        </Field>
                      </div>
                    </div>
                  )}

                  {/* ── STEP 2: Your Requirement (all optional) ── */}
                  {step === 1 && (
                    <div className="flex flex-col gap-5 md:gap-6">
                      <Field label="What is your primary requirement?">
                        <div className="flex flex-col gap-2.5">
                          <CheckRadio selected={primaryGoal.includes('training')} onClick={() => toggleGoal('training')}>
                            Training Data for AI Models
                          </CheckRadio>
                          <CheckRadio selected={primaryGoal.includes('licensing')} onClick={() => toggleGoal('licensing')}>
                            Licensing Voices for Customer-Facing Products
                          </CheckRadio>
                        </div>
                      </Field>

                      <Field label="Languages">
                        <div ref={langRef} className="relative">
                          <button type="button" onClick={() => setLangOpen(!langOpen)} className="w-full flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-xl py-2.5 px-4 text-sm text-left transition-colors focus:outline-none focus:border-[#56CFFC]/50">
                            <span className={languages.length ? 'text-white' : 'text-gray-600'}>
                              {languages.length ? `${languages.length} selected` : 'Select languages'}
                            </span>
                            <ChevronDown size={14} className={`text-gray-500 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
                          </button>
                          <AnimatePresence>
                            {langOpen && (
                              <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.15 }} className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#0E0E1A] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                                <div className="p-2 border-b border-white/5">
                                  <div className="flex items-center gap-2 px-2 py-1 bg-white/5 rounded-lg">
                                    <Search size={12} className="text-gray-500 shrink-0" />
                                    <input type="text" value={langSearch} onChange={e => setLangSearch(e.target.value)} placeholder="Search languages..." className="bg-transparent text-white text-xs focus:outline-none w-full placeholder:text-gray-600" autoFocus />
                                  </div>
                                </div>
                                <div className="max-h-44 overflow-y-auto">
                                  {filteredLangs.map(lang => (
                                    <button key={lang} type="button" onClick={() => toggleLanguage(lang)} className="w-full flex items-center gap-3 px-4 py-2 text-xs text-left hover:bg-white/5 transition-colors">
                                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${languages.includes(lang) ? 'bg-[#56CFFC]/20 border-[#56CFFC]/50' : 'border-white/20'}`}>
                                        {languages.includes(lang) && <Check size={10} className="text-[#56CFFC]" />}
                                      </div>
                                      <span className={languages.includes(lang) ? 'text-white' : 'text-gray-400'}>{lang}</span>
                                    </button>
                                  ))}
                                  {filteredLangs.length === 0 && <p className="text-gray-600 text-xs text-center py-3">No matches</p>}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                          {languages.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {languages.map(lang => (
                                <span key={lang} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#56CFFC]/10 border border-[#56CFFC]/20 text-[#56CFFC] text-[10px] font-medium">
                                  {lang}
                                  <button type="button" onClick={() => toggleLanguage(lang)} className="hover:text-white transition-colors"><X size={10} /></button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </Field>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Estimated Volume">
                          <StyledSelect value={volume} onChange={setVolume} options={VOLUMES} placeholder="Select volume" />
                        </Field>
                        <Field label="Timeline">
                          <StyledSelect value={timeline} onChange={setTimeline} options={TIMELINES} placeholder="Select timeline" />
                        </Field>
                        <Field label="Budget" optional>
                          <StyledSelect value={budget} onChange={setBudget} options={BUDGETS} placeholder="Select range" />
                        </Field>
                      </div>
                    </div>
                  )}

                  {/* ── STEP 3: Anything Else + File Upload ── */}
                  {step === 2 && (
                    <div className="flex flex-col gap-5 md:gap-6">
                      <Field label="Tell us about any edge cases or specific requirements">
                        <textarea
                          value={details}
                          onChange={e => setDetails(e.target.value)}
                          rows={4}
                          placeholder="e.g., We need heavy background noise simulation, or 60% female representation in the 18-35 demographic, specific emotional ranges for customer service scenarios..."
                          className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-3 px-4 text-white text-sm focus:outline-none focus:border-[#56CFFC]/50 transition-colors placeholder:text-gray-600 resize-none"
                        />
                      </Field>

                      {/* File Upload — Advanced FilePond (up to 50 MB) */}
                      <Field label="Attachments (Optional)">
                        <div className="filepond-dark">
                          <FilePond
                            files={pondFiles}
                            onupdatefiles={setPondFiles}
                            onprocessfile={(_error, file) => { if (file.serverId) setUploadedKeys(prev => [...prev, file.serverId as string]); }}
                            onremovefile={(_error, file) => { if (file.serverId) setUploadedKeys(prev => prev.filter(k => k !== file.serverId)); }}
                            allowMultiple={true}
                            maxFiles={5}
                            maxFileSize="50MB"
                            name="attachment"
                            server={filepondServer}
                            labelIdle='Drag & drop files or <span class="filepond--label-action">Browse</span> — up to 50 MB'
                            credits={false}
                          />
                        </div>
                      </Field>

                      {/* Quick summary */}
                      <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
                        <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-2 font-bold">Quick Summary</p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                          {name && <SummaryRow label="Name" value={name} />}
                          {email && <SummaryRow label="Email" value={email} />}
                          {company && <SummaryRow label="Company" value={company} />}
                          {primaryGoal.length > 0 && <SummaryRow label="Goal" value={primaryGoal.map(g => g === 'training' ? 'Training' : 'Licensing').join(' + ')} />}
                          {languages.length > 0 && <SummaryRow label="Languages" value={`${languages.length} selected`} />}
                          {volume && <SummaryRow label="Volume" value={volume} />}
                          {uploadedKeys.length > 0 && <SummaryRow label="Files" value={`${uploadedKeys.length} attached`} />}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* ── Footer nav ── */}
            <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] py-4 md:py-5 border-t border-white/5 shrink-0 flex items-center gap-3">
              {step < 2 ? (
                <button
                  type="button"
                  onClick={next}
                  disabled={step === 0 && !canProceedStep0}
                  className="group relative flex-1 py-3 md:py-3.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[10px] md:text-[11px] tracking-[0.2em] uppercase overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] disabled:opacity-40 disabled:hover:shadow-none flex items-center justify-center"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                  <span className="relative z-10 flex items-center gap-2">
                    {step === 0 ? 'Continue' : 'Skip or Continue'}
                    <ChevronRight size={14} />
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!!emailError || !canProceedStep0 || submitting}
                  className="group relative flex-1 py-3 md:py-3.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[10px] md:text-[11px] tracking-[0.2em] uppercase overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] disabled:opacity-40 flex items-center justify-center"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                  <span className="relative z-10 flex items-center gap-2">
                    {submitting ? 'Submitting...' : 'Submit Project Brief'}
                    {!submitting && <ArrowRight size={14} />}
                  </span>
                </button>
              )}
              <p className="text-[9px] md:text-[10px] text-gray-600 hidden md:block whitespace-nowrap">
                {step < 2 ? `Step ${step + 1} of 3` : "We'll respond within 24 hours."}
              </p>
            </div>
            </>
            )}
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════════════
   Shared overlay shell — reused by Talent & Studio forms
   ═══════════════════════════════════════════════════════════ */
function OverlayShell({ isOpen, onClose, title, children }: { isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode }) {
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
    } else {
      setAnimateIn(false);
    }
  }, [isOpen]);

  // Trigger animateIn after the portal is painted
  useEffect(() => {
    if (visible && isOpen) {
      const id = setTimeout(() => setAnimateIn(true), 20);
      return () => clearTimeout(id);
    }
  }, [visible, isOpen]);

  const handleTransitionEnd = useCallback(() => {
    if (!isOpen) setVisible(false);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const sc = document.getElementById('main-scroll-container');
      if (sc) sc.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      return () => {
        if (sc) sc.style.overflow = '';
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!visible) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex transition-opacity duration-300"
      style={{ opacity: animateIn ? 1 : 0, pointerEvents: animateIn ? 'auto' : 'none' }}
      onTransitionEnd={(e) => { if (e.propertyName === 'opacity' && !animateIn) handleTransitionEnd(); }}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[12px] transition-opacity duration-300"
        style={{ opacity: animateIn ? 1 : 0 }}
        onClick={onClose}
      />
      <div
        className="relative z-10 ml-auto w-full md:w-[clamp(500px,45vw,720px)] h-full md:h-auto md:my-[clamp(60px,5.5vw,96px)] md:mr-[clamp(16px,1.5vw,28px)] md:rounded-[clamp(20px,1.85vw,32px)] flex flex-col overflow-hidden transition-transform duration-300 ease-out"
        style={{
          background: 'linear-gradient(180deg, rgba(8,8,20,0.97) 0%, rgba(6,6,16,0.98) 100%)',
          transform: animateIn ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        <div className="hidden md:block absolute inset-0 rounded-[inherit] pointer-events-none z-10" style={{
          padding: '1.5px',
          background: 'linear-gradient(135deg, rgba(86,207,252,0.5) 0%, rgba(27,54,255,0.3) 50%, rgba(86,207,252,0.25) 100%)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
        }} />
        <div className="md:hidden absolute left-0 top-0 bottom-0 w-[1.5px] bg-gradient-to-b from-[#56CFFC]/40 via-[#1B36FF]/30 to-transparent" />
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[radial-gradient(circle,rgba(86,207,252,0.04)_0%,transparent_70%)] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[250px] h-[250px] bg-[radial-gradient(circle,rgba(27,54,255,0.06)_0%,transparent_70%)] pointer-events-none" />

        {/* Header */}
        <div className="relative flex items-center justify-between px-5 md:px-[clamp(28px,2.5vw,40px)] pt-5 md:pt-[clamp(20px,2vw,28px)] pb-4 md:pb-[clamp(14px,1.2vw,20px)] border-b border-white/5 shrink-0">
          <button onClick={onClose} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full border border-white/10 group-hover:border-white/20 flex items-center justify-center transition-colors">
              <ChevronLeft size={16} />
            </div>
            <span className="text-[10px] md:text-[clamp(9px,0.69vw,11px)] font-bold uppercase tracking-widest">Close</span>
          </button>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 transition-colors text-gray-500 hover:text-white border border-white/5">
            <X size={14} />
          </button>
        </div>

        {/* Title */}
        <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] pt-5 md:pt-6 pb-2 shrink-0">
          <h1 className="text-white font-black text-[clamp(20px,1.8vw,32px)] tracking-tight uppercase">{title}</h1>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-y-contain">
        {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════════════
   Talent Form Overlay — "Join Our Roster"
   ═══════════════════════════════════════════════════════════ */
const EXPERIENCE_LEVELS = ['Under 1 Year', '1–3 Years', '3–5 Years', '5–10 Years', '10+ Years'];

export function TalentFormOverlay({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [langOpen, setLangOpen] = useState(false);
  const [langSearch, setLangSearch] = useState('');
  const [experience, setExperience] = useState('');
  const [audioPondFiles, setAudioPondFiles] = useState<any[]>([]);
  const [audioUploadedKey, setAudioUploadedKey] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) setSubmitted(false);
  }, [isOpen]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleLanguage = (lang: string) => {
    setLanguages(prev => prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]);
  };

  const filteredLangs = LANGUAGES.filter(l => l.toLowerCase().includes(langSearch.toLowerCase()));

  const canSubmit = name && email && languages.length > 0 && experience;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload: Record<string, string> = {
        access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
        subject: `New Talent Application — ${name}`,
        from_name: 'Voqals Talent Form',
        replyto: email,
        Name: name,
        Email: email,
        Phone: phone || '-',
        Languages: languages.join(', '),
        Experience: experience,
      };
      if (audioUploadedKey) payload.attachment = audioUploadedKey;

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then(r => r.json());
      console.log('Talent form response:', res);
      if (res.success) {
        setSubmitted(true);
        window.gtag?.('event', 'conversion', { send_to: 'AW-18066710209/65G5CK-OyJYcEMG98KZD', value: 1.0, currency: 'INR' });
      } else {
        console.error('Talent form rejected:', res);
        alert('Submission failed: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Talent submission error:', err);
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OverlayShell isOpen={isOpen} onClose={onClose} title="Join Our Roster">
      {submitted ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center mb-6">
            <Check size={28} className="text-[#56CFFC]" />
          </div>
          <h2 className="text-white font-black text-[clamp(22px,2vw,36px)] tracking-tight uppercase mb-3">Application Received</h2>
          <p className="text-white/80 text-[clamp(12px,0.9vw,15px)] leading-relaxed max-w-sm mb-8">
            Thank you, {name.split(' ')[0]}. We'll review your profile and reach out if there's a match.
          </p>
          <button type="button" onClick={onClose} className="px-8 py-3 rounded-full border border-white/10 text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white/5 transition-colors">
            Close
          </button>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 md:px-[clamp(28px,2.5vw,40px)] pt-4 md:pt-5 pb-6">
            <div className="flex flex-col gap-5 md:gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <Field label="Name">
                  <Input value={name} onChange={setName} placeholder="Jane Doe" required />
                </Field>
                <Field label="Email">
                  <Input value={email} onChange={setEmail} placeholder="jane@email.com" type="email" required />
                </Field>
              </div>
              <Field label="Phone" optional>
                <Input value={phone} onChange={setPhone} placeholder="+91 98765 43210" type="tel" />
              </Field>

              <Field label="Languages you speak fluently">
                <div ref={langRef} className="relative">
                  <button type="button" onClick={() => setLangOpen(!langOpen)} className="w-full flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-xl py-2.5 px-4 text-sm text-left transition-colors focus:outline-none focus:border-[#56CFFC]/50">
                    <span className={languages.length ? 'text-white' : 'text-gray-600'}>
                      {languages.length ? `${languages.length} selected` : 'Select languages'}
                    </span>
                    <ChevronDown size={14} className={`text-gray-500 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {langOpen && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#0E0E1A] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                      <div className="p-2 border-b border-white/5">
                        <div className="flex items-center gap-2 px-2 py-1 bg-white/5 rounded-lg">
                          <Search size={12} className="text-gray-500 shrink-0" />
                          <input type="text" value={langSearch} onChange={e => setLangSearch(e.target.value)} placeholder="Search languages..." className="bg-transparent text-white text-xs focus:outline-none w-full placeholder:text-gray-600" autoFocus />
                        </div>
                      </div>
                      <div className="max-h-44 overflow-y-auto">
                        {filteredLangs.map(lang => (
                          <button key={lang} type="button" onClick={() => toggleLanguage(lang)} className="w-full flex items-center gap-3 px-4 py-2 text-xs text-left hover:bg-white/5 transition-colors">
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${languages.includes(lang) ? 'bg-[#56CFFC]/20 border-[#56CFFC]/50' : 'border-white/20'}`}>
                              {languages.includes(lang) && <Check size={10} className="text-[#56CFFC]" />}
                            </div>
                            <span className={languages.includes(lang) ? 'text-white' : 'text-gray-400'}>{lang}</span>
                          </button>
                        ))}
                        {filteredLangs.length === 0 && <p className="text-gray-600 text-xs text-center py-3">No matches</p>}
                      </div>
                    </div>
                  )}
                  {languages.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {languages.map(lang => (
                        <span key={lang} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#56CFFC]/10 border border-[#56CFFC]/20 text-[#56CFFC] text-[10px] font-medium">
                          {lang}
                          <button type="button" onClick={() => toggleLanguage(lang)} className="hover:text-white transition-colors"><X size={10} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Experience Level">
                <StyledSelect value={experience} onChange={setExperience} options={EXPERIENCE_LEVELS} placeholder="Select experience" required />
              </Field>

              <Field label="Audio Sample" optional>
                <div className="filepond-dark">
                  <FilePond
                    files={audioPondFiles}
                    onupdatefiles={setAudioPondFiles}
                    onprocessfile={(_error, file) => { if (file.serverId) setAudioUploadedKey(file.serverId as string); }}
                    onremovefile={() => setAudioUploadedKey('')}
                    allowMultiple={false}
                    maxFiles={1}
                    maxFileSize="50MB"
                    acceptedFileTypes={['audio/mpeg', 'audio/wav', 'audio/flac', 'audio/aac', 'audio/mp4', 'audio/ogg', 'audio/x-m4a']}
                    name="attachment"
                    server={filepondServer}
                    labelIdle='Drag & drop audio or <span class="filepond--label-action">Browse</span> — up to 50 MB'
                    credits={false}
                  />
                </div>
                <p className="text-[9px] text-gray-600 mt-1 tracking-wide">Upload a clean vocal sample — no background music, effects, or processing. We want to hear your natural voice at its best.</p>
              </Field>
            </div>
          </div>

          <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] py-4 md:py-5 border-t border-white/5 shrink-0">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="group relative w-full py-3 md:py-3.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[10px] md:text-[11px] tracking-[0.2em] uppercase overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] disabled:opacity-40 disabled:hover:shadow-none flex items-center justify-center"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
              <span className="relative z-10 flex items-center gap-2">
                {submitting ? 'Submitting...' : 'Submit Application'}
                {!submitting && <ArrowRight size={14} />}
              </span>
            </button>
          </div>
        </>
      )}
    </OverlayShell>
  );
}

/* ═══════════════════════════════════════════════════════════
   Studio Form Overlay — "Get Voqals Certified"
   ═══════════════════════════════════════════════════════════ */
export function StudioFormOverlay({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [studioName, setStudioName] = useState('');
  const [location, setLocation] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSubmitted(false);
  }, [isOpen]);

  const canSubmit = contactName && email && studioName;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
        subject: `New Studio Application — ${studioName}`,
        from_name: 'Voqals Studio Form',
        replyto: email,
        'Contact Name': contactName,
        Email: email,
        Phone: phone || '-',
        'Studio Name': studioName,
        Location: location || '-',
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      console.log('Studio form response:', data);
      setSubmitted(true);
      window.gtag?.('event', 'conversion', { send_to: 'AW-18066710209/65G5CK-OyJYcEMG98KZD', value: 1.0, currency: 'INR' });
    } catch (err) {
      console.error('Studio submission error:', err);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OverlayShell isOpen={isOpen} onClose={onClose} title="Get Voqals Certified">
      {submitted ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center mb-6">
            <Check size={28} className="text-[#56CFFC]" />
          </div>
          <h2 className="text-white font-black text-[clamp(22px,2vw,36px)] tracking-tight uppercase mb-3">Application Received</h2>
          <p className="text-white/80 text-[clamp(12px,0.9vw,15px)] leading-relaxed max-w-sm mb-8">
            Thank you. We'll review your studio profile and be in touch soon.
          </p>
          <button type="button" onClick={onClose} className="px-8 py-3 rounded-full border border-white/10 text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white/5 transition-colors">
            Close
          </button>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 md:px-[clamp(28px,2.5vw,40px)] pt-4 md:pt-5 pb-6">
            <div className="flex flex-col gap-5 md:gap-6">
              <Field label="Contact Name">
                <Input value={contactName} onChange={setContactName} placeholder="Your full name" required />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Email">
                  <Input value={email} onChange={setEmail} placeholder="you@studio.com" type="email" required />
                </Field>
                <Field label="Phone" optional>
                  <Input value={phone} onChange={setPhone} placeholder="+91 98765 43210" type="tel" />
                </Field>
              </div>
              <Field label="Studio Name">
                <Input value={studioName} onChange={setStudioName} placeholder="e.g., Sunrise Recording Studios" required />
              </Field>
              <Field label="Studio Location">
                <Input value={location} onChange={setLocation} placeholder="City, State" />
              </Field>
            </div>
          </div>

          <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] py-4 md:py-5 border-t border-white/5 shrink-0">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="group relative w-full py-3 md:py-3.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[10px] md:text-[11px] tracking-[0.2em] uppercase overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] disabled:opacity-40 disabled:hover:shadow-none flex items-center justify-center"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
              <span className="relative z-10 flex items-center gap-2">
                {submitting ? 'Submitting...' : 'Submit Application'}
                {!submitting && <ArrowRight size={14} />}
              </span>
            </button>
          </div>
        </>
      )}
    </OverlayShell>
  );
}

/* ═══════════════════════════════════════════════════════════
   Studio Audio Test Overlay — "Request a Studio Audio Test"
   One-step form: basic details + city pills + what they'll receive
   Designed to fit a single viewport — no internal scroll.
   ═══════════════════════════════════════════════════════════ */
const INDIAN_CITIES = [
  'Mumbai', 'Delhi / NCR', 'Bangalore', 'Hyderabad', 'Chennai',
  'Kolkata', 'Pune', 'Ahmedabad', 'Lucknow', 'Jaipur',
  'Chandigarh', 'Kochi', 'Indore', 'Bhopal', 'Coimbatore',
  'Guwahati', 'Patna', 'Vizag', 'Other',
];

export function StudioAudioTestOverlay({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [company, setCompany] = useState('');
  const [city, setCity] = useState('');
  const [otherCity, setOtherCity] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSubmitted(false);
  }, [isOpen]);

  const validateEmail = (val: string) => {
    setEmail(val);
    if (!val) { setEmailError(''); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) { setEmailError('Please enter a valid email.'); return; }
    const domain = val.split('@')[1]?.toLowerCase();
    if (domain && FREE_DOMAINS.includes(domain)) {
      setEmailError('Please use your company email address.');
    } else { setEmailError(''); }
  };

  const resolvedCity = city === 'Other' ? otherCity : city;
  const canSubmit = name && email && !emailError && company && resolvedCity;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
        subject: `Studio Audio Test Request — ${company} (${resolvedCity})`,
        from_name: 'Voqals Studio Audio Test',
        replyto: email,
        Name: name,
        Email: email,
        Company: company,
        'City / Region': resolvedCity,
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then(r => r.json());
      console.log('Studio audio test response:', res);
      if (res.success) {
        setSubmitted(true);
        window.gtag?.('event', 'conversion', { send_to: 'AW-18066710209/65G5CK-OyJYcEMG98KZD', value: 1.0, currency: 'INR' });
      } else {
        console.error('Studio audio test rejected:', res);
        alert('Submission failed: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Studio audio test error:', err);
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OverlayShell isOpen={isOpen} onClose={onClose} title="Request a Studio Audio Test">
      {submitted ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center mb-6">
            <Check size={28} className="text-[#56CFFC]" />
          </div>
          <h2 className="text-white font-black text-[clamp(22px,2vw,36px)] tracking-tight uppercase mb-3">Request Received</h2>
          <p className="text-white/80 text-[clamp(12px,0.9vw,15px)] leading-relaxed max-w-sm mb-2">
            We'll record a test session in <span className="text-white font-semibold">{resolvedCity}</span> and send you the audio pack within 48 hours.
          </p>
          <p className="text-white/60 text-[clamp(10px,0.75vw,12px)] mb-8">
            A confirmation has been sent to <span className="text-white/80">{email}</span>
          </p>
          <button type="button" onClick={onClose} className="px-8 py-3 rounded-full border border-white/10 text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white/5 transition-colors">
            Close
          </button>
        </div>
      ) : (
        <>
          <div className="flex-1 px-5 md:px-[clamp(28px,2.5vw,40px)] pt-3 md:pt-4 pb-3 flex flex-col gap-3.5 md:gap-4">

            {/* What you'll receive — compact inline */}
            <div className="rounded-lg border border-[#56CFFC]/15 bg-[#56CFFC]/[0.03] px-3.5 py-2.5 md:px-4 md:py-3 flex items-center gap-3 flex-wrap">
              <span className="text-[8px] md:text-[clamp(7px,0.55vw,10px)] text-[#56CFFC] uppercase tracking-widest font-bold shrink-0">You'll receive</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {['Speech sample', 'Room tone', 'RT60 test'].map(item => (
                  <span key={item} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#56CFFC]/8 border border-[#56CFFC]/20 text-[9px] md:text-[clamp(8px,0.6vw,11px)] text-[#56CFFC]/90 font-medium whitespace-nowrap">
                    <Check size={8} className="text-[#56CFFC]" />
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Name + Company + Email — compact 3-col on desktop, stacked on mobile */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Your Name">
                <Input value={name} onChange={setName} placeholder="Jane Doe" required />
              </Field>
              <Field label="Company">
                <Input value={company} onChange={setCompany} placeholder="Acme AI" required />
              </Field>
              <Field label="Work Email">
                <Input value={email} onChange={validateEmail} placeholder="jane@company.com" type="email" error={!!emailError} required />
                {emailError && <p className="text-red-400 text-[10px] mt-0.5">{emailError}</p>}
              </Field>
            </div>

            {/* City — pill grid */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[9px] md:text-[clamp(8px,0.64vw,11px)] uppercase tracking-widest text-white/80 font-medium">
                Which city do you need a studio in?
              </label>
              <div className="flex flex-wrap gap-1.5">
                {INDIAN_CITIES.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => { setCity(c); if (c !== 'Other') setOtherCity(''); }}
                    className={`px-2.5 py-1 md:px-3 md:py-1.5 rounded-full text-[9px] md:text-[clamp(8px,0.6vw,11px)] font-bold tracking-[0.08em] uppercase transition-all duration-200 border ${
                      city === c
                        ? 'bg-[#56CFFC]/10 border-[#56CFFC]/50 text-[#56CFFC] shadow-[0_0_12px_rgba(86,207,252,0.15)]'
                        : 'bg-transparent border-white/10 text-gray-500 hover:text-white hover:border-white/25'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>

              {/* Conditional "Other" text input */}
              {city === 'Other' && (
                <input
                  type="text"
                  value={otherCity}
                  onChange={e => setOtherCity(e.target.value)}
                  placeholder="Type your city or region..."
                  autoFocus
                  className="mt-1.5 w-full bg-white/[0.03] border border-white/10 rounded-xl py-2 px-3.5 text-white text-sm focus:outline-none focus:border-[#56CFFC]/50 transition-colors placeholder:text-gray-600"
                />
              )}
              <p className="text-[8px] md:text-[clamp(8px,0.55vw,10px)] text-white/60 tracking-wide mt-0.5">Not sure which city? Pick the closest one — we'll help you find the best fit.</p>
            </div>
          </div>

          {/* Footer — submit */}
          <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] py-3 md:py-4 border-t border-white/5 shrink-0">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="group relative w-full py-2.5 md:py-3 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[10px] md:text-[11px] tracking-[0.2em] uppercase overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] disabled:opacity-40 disabled:hover:shadow-none flex items-center justify-center"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
              <span className="relative z-10 flex items-center gap-2">
                {submitting ? 'Submitting...' : 'Request Audio Test'}
                {!submitting && <ArrowRight size={14} />}
              </span>
            </button>

          </div>
        </>
      )}
    </OverlayShell>
  );
}

/* ═══════════════════════════════════════════════════════════
   Casting Requirements Overlay — "Request Talent Samples"
   One-step form: name, email, company, languages (pills),
   gender/age profile, volume, and notes.
   ═══════════════════════════════════════════════════════════ */
const PILL_LANGUAGES = [
  'Hindi', 'English (Indian)', 'Tamil', 'Telugu', 'Bengali', 'Marathi',
  'Gujarati', 'Kannada', 'Malayalam', 'Punjabi', 'Odia', 'Urdu', 'Other',
];
const GENDER_OPTIONS = ['Male', 'Female', 'Both', 'Non-binary / Other'];
const AGE_RANGES = ['18–25', '25–35', '35–50', '50+', 'Mixed / No preference'];

export function CastingRequirementsOverlay({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [company, setCompany] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [otherLanguage, setOtherLanguage] = useState('');
  const [gender, setGender] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSubmitted(false);
  }, [isOpen]);

  const validateEmail = (val: string) => {
    setEmail(val);
    if (!val) { setEmailError(''); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) { setEmailError('Please enter a valid email.'); return; }
    const domain = val.split('@')[1]?.toLowerCase();
    if (domain && FREE_DOMAINS.includes(domain)) {
      setEmailError('Please use your company email address.');
    } else { setEmailError(''); }
  };

  const toggleLang = (lang: string) => {
    setLanguages(prev => prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]);
    if (lang !== 'Other') setOtherLanguage('');
  };

  const resolvedLanguages = languages.map(l => l === 'Other' ? (otherLanguage || 'Other') : l);
  const canSubmit = name && email && !emailError && company && languages.length > 0 && (!languages.includes('Other') || otherLanguage) && gender && ageRange;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
        subject: `Casting Requirements — ${company} (${resolvedLanguages.join(', ')})`,
        from_name: 'Voqals Casting Request',
        replyto: email,
        Name: name,
        Email: email,
        Company: company,
        Languages: resolvedLanguages.join(', '),
        'Gender Profile': gender || '-',
        'Age Range': ageRange || '-',
        'Additional Notes': notes || '-',
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then(r => r.json());
      console.log('Casting form response:', res);
      if (res.success) {
        setSubmitted(true);
        window.gtag?.('event', 'conversion', { send_to: 'AW-18066710209/65G5CK-OyJYcEMG98KZD', value: 1.0, currency: 'INR' });
      } else {
        console.error('Casting form rejected:', res);
        alert('Submission failed: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Casting form error:', err);
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OverlayShell isOpen={isOpen} onClose={onClose} title="Request Talent Samples">
      {submitted ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center mb-6">
            <Check size={28} className="text-[#56CFFC]" />
          </div>
          <h2 className="text-white font-black text-[clamp(22px,2vw,36px)] tracking-tight uppercase mb-3">Request Received</h2>
          <p className="text-white/80 text-[clamp(12px,0.9vw,15px)] leading-relaxed max-w-sm mb-2">
            We'll curate sample auditions for <span className="text-white font-semibold">{resolvedLanguages.join(', ')}</span> and send them to you within 48 hours.
          </p>
          <p className="text-white/60 text-[clamp(10px,0.75vw,12px)] mb-8">
            A confirmation has been sent to <span className="text-white/80">{email}</span>
          </p>
          <button type="button" onClick={onClose} className="px-8 py-3 rounded-full border border-white/10 text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white/5 transition-colors">
            Close
          </button>
        </div>
      ) : (
        <>
          <div className="flex-1 px-5 md:px-[clamp(28px,2.5vw,40px)] pt-3 md:pt-4 pb-3 flex flex-col gap-3 md:gap-3.5">

            {/* Name + Company + Email */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Your Name">
                <Input value={name} onChange={setName} placeholder="Jane Doe" required />
              </Field>
              <Field label="Company">
                <Input value={company} onChange={setCompany} placeholder="Acme AI" required />
              </Field>
              <Field label="Work Email">
                <Input value={email} onChange={validateEmail} placeholder="jane@company.com" type="email" error={!!emailError} required />
                {emailError && <p className="text-red-400 text-[10px] mt-0.5">{emailError}</p>}
              </Field>
            </div>

            {/* Languages — pills (multi-select) */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[9px] md:text-[clamp(8px,0.64vw,11px)] uppercase tracking-widest text-white/80 font-medium">
                Languages you need talent for
              </label>
              <div className="flex flex-wrap gap-1.5">
                {PILL_LANGUAGES.map(lang => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => toggleLang(lang)}
                    className={`px-2.5 py-1 md:px-3 md:py-1.5 rounded-full text-[9px] md:text-[clamp(8px,0.6vw,11px)] font-bold tracking-[0.08em] uppercase transition-all duration-200 border ${
                      languages.includes(lang)
                        ? 'bg-[#56CFFC]/10 border-[#56CFFC]/50 text-[#56CFFC] shadow-[0_0_12px_rgba(86,207,252,0.15)]'
                        : 'bg-transparent border-white/10 text-gray-500 hover:text-white hover:border-white/25'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
              {languages.includes('Other') && (
                <input
                  type="text"
                  value={otherLanguage}
                  onChange={e => setOtherLanguage(e.target.value)}
                  placeholder="Type the language(s)..."
                  autoFocus
                  className="mt-1.5 w-full bg-white/[0.03] border border-white/10 rounded-xl py-2 px-3.5 text-white text-sm focus:outline-none focus:border-[#56CFFC]/50 transition-colors placeholder:text-gray-600"
                />
              )}
            </div>

            {/* Gender + Age — pills side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-[9px] md:text-[clamp(8px,0.64vw,11px)] uppercase tracking-widest text-white/80 font-medium">
                  Gender profile
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {GENDER_OPTIONS.map(g => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGender(prev => prev === g ? '' : g)}
                      className={`px-2.5 py-1 rounded-full text-[9px] md:text-[clamp(8px,0.6vw,11px)] font-bold tracking-[0.08em] uppercase transition-all duration-200 border ${
                        gender === g
                          ? 'bg-[#56CFFC]/10 border-[#56CFFC]/50 text-[#56CFFC]'
                          : 'bg-transparent border-white/10 text-gray-500 hover:text-white hover:border-white/25'
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[9px] md:text-[clamp(8px,0.64vw,11px)] uppercase tracking-widest text-white/80 font-medium">
                  Age range
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {AGE_RANGES.map(a => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setAgeRange(prev => prev === a ? '' : a)}
                      className={`px-2.5 py-1 rounded-full text-[9px] md:text-[clamp(8px,0.6vw,11px)] font-bold tracking-[0.08em] uppercase transition-all duration-200 border ${
                        ageRange === a
                          ? 'bg-[#56CFFC]/10 border-[#56CFFC]/50 text-[#56CFFC]'
                          : 'bg-transparent border-white/10 text-gray-500 hover:text-white hover:border-white/25'
                      }`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Notes — compact */}
            <Field label="Anything else?" optional>
              <input
                type="text"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g., need native speakers only, specific dialect, emotional range..."
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-2 px-3.5 text-white text-sm focus:outline-none focus:border-[#56CFFC]/50 transition-colors placeholder:text-gray-600"
              />
            </Field>
          </div>

          {/* Footer — submit */}
          <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] py-3 md:py-4 border-t border-white/5 shrink-0">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="group relative w-full py-2.5 md:py-3 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[10px] md:text-[11px] tracking-[0.2em] uppercase overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] disabled:opacity-40 disabled:hover:shadow-none flex items-center justify-center"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
              <span className="relative z-10 flex items-center gap-2">
                {submitting ? 'Submitting...' : 'Request Talent Samples'}
                {!submitting && <ArrowRight size={14} />}
              </span>
            </button>
          </div>
        </>
      )}
    </OverlayShell>
  );
}

/* ═══════════════════════════════════════════════════════════
   Script Sample — one-step overlay for Scripting section CTA
   ═══════════════════════════════════════════════════════════ */

const SCRIPT_ARCHETYPES = [
  'Spontaneous Speech', 'Solo Monologues', 'Assistant ↔ User', 'Multi-Speaker',
  'Foundational Prompts', 'Code-Switched Dialogue', 'Emotion-Specific', 'Task-Oriented Commands',
  'Read Speech', 'Domain-Specific', 'Disfluency-Rich', 'Wake-Word & Triggers',
];


export function ScriptSampleOverlay({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [company, setCompany] = useState('');
  const [scriptTypes, setScriptTypes] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [otherLanguage, setOtherLanguage] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSubmitted(false);
  }, [isOpen]);

  const validateEmail = (val: string) => {
    setEmail(val);
    if (!val) { setEmailError(''); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) { setEmailError('Please enter a valid email.'); return; }
    const domain = val.split('@')[1]?.toLowerCase();
    if (domain && FREE_DOMAINS.includes(domain)) {
      setEmailError('Please use your company email address.');
    } else { setEmailError(''); }
  };

  const toggleType = (t: string) => {
    setScriptTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const toggleLang = (lang: string) => {
    setLanguages(prev => prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]);
    if (lang !== 'Other') setOtherLanguage('');
  };

  const resolvedLanguages = languages.map(l => l === 'Other' ? (otherLanguage || 'Other') : l);
  const canSubmit = name && email && !emailError && company && scriptTypes.length > 0 && languages.length > 0 && (!languages.includes('Other') || otherLanguage);

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
        subject: `Script Sample Request — ${company} (${scriptTypes.join(', ')})`,
        from_name: 'Voqals Script Sample Request',
        replyto: email,
        Name: name,
        Email: email,
        Company: company,
        'Script Types': scriptTypes.join(', '),
        Languages: resolvedLanguages.join(', '),
        'Additional Notes': notes || '-',
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then(r => r.json());
      console.log('Script sample form response:', res);
      if (res.success) {
        setSubmitted(true);
        window.gtag?.('event', 'conversion', { send_to: 'AW-18066710209/65G5CK-OyJYcEMG98KZD', value: 1.0, currency: 'INR' });
      } else {
        console.error('Script sample form rejected:', res);
        alert('Submission failed: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Script sample form error:', err);
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OverlayShell isOpen={isOpen} onClose={onClose} title="Get a Sample Script">
      {submitted ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center mb-6">
            <Check size={28} className="text-[#56CFFC]" />
          </div>
          <h2 className="text-white font-black text-[clamp(22px,2vw,36px)] tracking-tight uppercase mb-3">Request Received</h2>
          <p className="text-white/80 text-[clamp(12px,0.9vw,15px)] leading-relaxed max-w-sm mb-2">
            We'll prepare a sample script pack for <span className="text-white font-semibold">{scriptTypes.join(', ')}</span> in <span className="text-white font-semibold">{resolvedLanguages.join(', ')}</span> and send it to you within 48 hours.
          </p>
          <p className="text-white/60 text-[clamp(10px,0.75vw,12px)] mb-8">
            A confirmation has been sent to <span className="text-white/80">{email}</span>
          </p>
          <button type="button" onClick={onClose} className="px-8 py-3 rounded-full border border-white/10 text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white/5 transition-colors">
            Close
          </button>
        </div>
      ) : (
        <>
          <div className="flex-1 px-5 md:px-[clamp(28px,2.5vw,40px)] pt-3 md:pt-4 pb-3 flex flex-col gap-3 md:gap-3.5">

            {/* Name + Company + Email */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Your Name">
                <Input value={name} onChange={setName} placeholder="Jane Doe" required />
              </Field>
              <Field label="Company">
                <Input value={company} onChange={setCompany} placeholder="Acme AI" required />
              </Field>
              <Field label="Work Email">
                <Input value={email} onChange={validateEmail} placeholder="jane@company.com" type="email" error={!!emailError} required />
                {emailError && <p className="text-red-400 text-[10px] mt-0.5">{emailError}</p>}
              </Field>
            </div>

            {/* Script Archetypes — pills (multi-select) */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[9px] md:text-[clamp(8px,0.64vw,11px)] uppercase tracking-widest text-white/80 font-medium">
                Script types you're interested in
              </label>
              <div className="flex flex-wrap gap-1.5">
                {SCRIPT_ARCHETYPES.map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleType(t)}
                    className={`px-2.5 py-1 md:px-3 md:py-1.5 rounded-full text-[9px] md:text-[clamp(8px,0.6vw,11px)] font-bold tracking-[0.08em] uppercase transition-all duration-200 border ${
                      scriptTypes.includes(t)
                        ? 'bg-[#56CFFC]/10 border-[#56CFFC]/50 text-[#56CFFC] shadow-[0_0_12px_rgba(86,207,252,0.15)]'
                        : 'bg-transparent border-white/10 text-gray-500 hover:text-white hover:border-white/25'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Languages — pills (multi-select) */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[9px] md:text-[clamp(8px,0.64vw,11px)] uppercase tracking-widest text-white/80 font-medium">
                Languages for sample script
              </label>
              <div className="flex flex-wrap gap-1.5">
                {PILL_LANGUAGES.map(l => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => toggleLang(l)}
                    className={`px-2.5 py-1 md:px-3 md:py-1.5 rounded-full text-[9px] md:text-[clamp(8px,0.6vw,11px)] font-bold tracking-[0.08em] uppercase transition-all duration-200 border ${
                      languages.includes(l)
                        ? 'bg-[#56CFFC]/10 border-[#56CFFC]/50 text-[#56CFFC] shadow-[0_0_12px_rgba(86,207,252,0.15)]'
                        : 'bg-transparent border-white/10 text-gray-500 hover:text-white hover:border-white/25'
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              {languages.includes('Other') && (
                <input
                  type="text"
                  value={otherLanguage}
                  onChange={e => setOtherLanguage(e.target.value)}
                  placeholder="Type the language(s)..."
                  autoFocus
                  className="mt-1.5 w-full bg-white/[0.03] border border-white/10 rounded-xl py-2 px-3.5 text-white text-sm focus:outline-none focus:border-[#56CFFC]/50 transition-colors placeholder:text-gray-600"
                />
              )}
            </div>

            {/* Notes — compact */}
            <Field label="Anything else?" optional>
              <input
                type="text"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g., specific domain, target model type, preferred tone..."
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-2 px-3.5 text-white text-sm focus:outline-none focus:border-[#56CFFC]/50 transition-colors placeholder:text-gray-600"
              />
            </Field>
          </div>

          {/* Footer — submit */}
          <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] py-3 md:py-4 border-t border-white/5 shrink-0">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="group relative w-full py-2.5 md:py-3 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[10px] md:text-[11px] tracking-[0.2em] uppercase overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] disabled:opacity-40 disabled:hover:shadow-none flex items-center justify-center"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
              <span className="relative z-10 flex items-center gap-2">
                {submitting ? 'Submitting...' : 'Get Sample Script'}
                {!submitting && <ArrowRight size={14} />}
              </span>
            </button>
          </div>
        </>
      )}
    </OverlayShell>
  );
}

/* ═══════════════════════════════════════════════════════════
   Live Session — one-step overlay for Recording section CTA
   Book a remote seat at a live recording session
   ═══════════════════════════════════════════════════════════ */

const TIME_ZONES = [
  'IST (UTC+5:30)', 'EST (UTC-5)', 'CST (UTC-6)', 'PST (UTC-8)',
  'GMT (UTC+0)', 'CET (UTC+1)', 'JST (UTC+9)', 'AEST (UTC+10)', 'Other',
];

export function LiveSessionOverlay({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [company, setCompany] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [otherTimeZone, setOtherTimeZone] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSubmitted(false);
  }, [isOpen]);

  const validateEmail = (val: string) => {
    setEmail(val);
    if (!val) { setEmailError(''); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) { setEmailError('Please enter a valid email.'); return; }
    const domain = val.split('@')[1]?.toLowerCase();
    if (domain && FREE_DOMAINS.includes(domain)) {
      setEmailError('Please use your company email address.');
    } else { setEmailError(''); }
  };

  const resolvedTimeZone = timeZone === 'Other' ? otherTimeZone : timeZone;
  const canSubmit = name && email && !emailError && company && preferredDate && resolvedTimeZone && preferredTime;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
        subject: `Live Session Request — ${company} (${preferredDate})`,
        from_name: 'Voqals Live Session Request',
        replyto: email,
        Name: name,
        Email: email,
        Company: company,
        'Preferred Date': preferredDate,
        'Time Zone': resolvedTimeZone,
        'Preferred Time': preferredTime,
        'Additional Notes': notes || '-',
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then(r => r.json());
      console.log('Live session form response:', res);
      if (res.success) {
        setSubmitted(true);
        window.gtag?.('event', 'conversion', { send_to: 'AW-18066710209/65G5CK-OyJYcEMG98KZD', value: 1.0, currency: 'INR' });
      } else {
        console.error('Live session form rejected:', res);
        alert('Submission failed: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Live session form error:', err);
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Get tomorrow's date as min for the date picker
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <OverlayShell isOpen={isOpen} onClose={onClose} title="Sit In on a Live Session">
      {submitted ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center mb-6">
            <Check size={28} className="text-[#56CFFC]" />
          </div>
          <h2 className="text-white font-black text-[clamp(22px,2vw,36px)] tracking-tight uppercase mb-3">Request Received</h2>
          <p className="text-white/80 text-[clamp(12px,0.9vw,15px)] leading-relaxed max-w-sm mb-2">
            We'll confirm your session for <span className="text-white font-semibold">{preferredDate}</span> at <span className="text-white font-semibold">{preferredTime}</span> ({resolvedTimeZone}) shortly.
          </p>
          <p className="text-white/60 text-[clamp(10px,0.75vw,12px)] mb-8">
            A confirmation will be sent to <span className="text-white/80">{email}</span>
          </p>
          <button type="button" onClick={onClose} className="px-8 py-3 rounded-full border border-white/10 text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white/5 transition-colors">
            Close
          </button>
        </div>
      ) : (
        <>
          <div className="flex-1 px-5 md:px-[clamp(28px,2.5vw,40px)] pt-3 md:pt-4 pb-3 flex flex-col gap-3 md:gap-3.5">

            {/* Name + Company + Email */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Your Name">
                <Input value={name} onChange={setName} placeholder="Jane Doe" required />
              </Field>
              <Field label="Company">
                <Input value={company} onChange={setCompany} placeholder="Acme AI" required />
              </Field>
              <Field label="Work Email">
                <Input value={email} onChange={validateEmail} placeholder="jane@company.com" type="email" error={!!emailError} required />
                {emailError && <p className="text-red-400 text-[10px] mt-0.5">{emailError}</p>}
              </Field>
            </div>

            {/* Preferred Date */}
            <Field label="Preferred Date">
              <input
                type="date"
                value={preferredDate}
                onChange={e => setPreferredDate(e.target.value)}
                min={minDate}
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-2.5 px-4 text-white text-sm md:text-[clamp(12px,0.85vw,15px)] focus:outline-none focus:border-[#56CFFC]/50 transition-colors [color-scheme:dark]"
              />
            </Field>

            {/* Time Zone — pills */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[9px] md:text-[clamp(8px,0.64vw,11px)] uppercase tracking-widest text-white/80 font-medium">
                Your time zone
              </label>
              <div className="flex flex-wrap gap-1.5">
                {TIME_ZONES.map(tz => (
                  <button
                    key={tz}
                    type="button"
                    onClick={() => { setTimeZone(prev => prev === tz ? '' : tz); if (tz !== 'Other') setOtherTimeZone(''); }}
                    className={`px-2.5 py-1 md:px-3 md:py-1.5 rounded-full text-[9px] md:text-[clamp(8px,0.6vw,11px)] font-bold tracking-[0.08em] uppercase transition-all duration-200 border ${
                      timeZone === tz
                        ? 'bg-[#56CFFC]/10 border-[#56CFFC]/50 text-[#56CFFC] shadow-[0_0_12px_rgba(86,207,252,0.15)]'
                        : 'bg-transparent border-white/10 text-gray-500 hover:text-white hover:border-white/25'
                    }`}
                  >
                    {tz}
                  </button>
                ))}
              </div>
              {timeZone === 'Other' && (
                <input
                  type="text"
                  value={otherTimeZone}
                  onChange={e => setOtherTimeZone(e.target.value)}
                  placeholder="e.g., SGT (UTC+8)"
                  autoFocus
                  className="mt-1.5 w-full bg-white/[0.03] border border-white/10 rounded-xl py-2 px-3.5 text-white text-sm focus:outline-none focus:border-[#56CFFC]/50 transition-colors placeholder:text-gray-600"
                />
              )}
            </div>

            {/* Preferred Time — pills */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[9px] md:text-[clamp(8px,0.64vw,11px)] uppercase tracking-widest text-white/80 font-medium">
                Preferred time
              </label>
              <div className="flex flex-wrap gap-1.5">
                {['Morning (9–12)', 'Afternoon (12–4)', 'Evening (4–7)', 'Flexible'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setPreferredTime(prev => prev === t ? '' : t)}
                    className={`px-2.5 py-1 md:px-3 md:py-1.5 rounded-full text-[9px] md:text-[clamp(8px,0.6vw,11px)] font-bold tracking-[0.08em] uppercase transition-all duration-200 border ${
                      preferredTime === t
                        ? 'bg-[#56CFFC]/10 border-[#56CFFC]/50 text-[#56CFFC] shadow-[0_0_12px_rgba(86,207,252,0.15)]'
                        : 'bg-transparent border-white/10 text-gray-500 hover:text-white hover:border-white/25'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes — compact */}
            <Field label="Anything else?" optional>
              <input
                type="text"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g., specific language, session type, number of attendees..."
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-2 px-3.5 text-white text-sm focus:outline-none focus:border-[#56CFFC]/50 transition-colors placeholder:text-gray-600"
              />
            </Field>

            {/* Info note */}
            <p className="text-[9px] md:text-[10px] text-white/60 leading-snug">
              Sessions run in Voqals-certified booths across India. We'll confirm availability and send you a remote link within 24 hours.
            </p>
          </div>

          {/* Footer — submit */}
          <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] py-3 md:py-4 border-t border-white/5 shrink-0">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="group relative w-full py-2.5 md:py-3 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[10px] md:text-[11px] tracking-[0.2em] uppercase overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] disabled:opacity-40 disabled:hover:shadow-none flex items-center justify-center"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
              <span className="relative z-10 flex items-center gap-2">
                {submitting ? 'Submitting...' : 'Request a Seat'}
                {!submitting && <ArrowRight size={14} />}
              </span>
            </button>
          </div>
        </>
      )}
    </OverlayShell>
  );
}

/* ═══════════════════════════════════════════════════════════
   Token Pack — one-step overlay for Post-Production section CTA
   Request a sample of production-ready, cleaned tokens
   ═══════════════════════════════════════════════════════════ */

const DELIVERY_FORMATS = ['WAV (16-bit / 48 kHz)', 'WAV (24-bit / 48 kHz)', 'FLAC', 'MP3 (320 kbps)', 'No preference'];

export function TokenPackOverlay({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [company, setCompany] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [otherLanguage, setOtherLanguage] = useState('');
  const [format, setFormat] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSubmitted(false);
  }, [isOpen]);

  const validateEmail = (val: string) => {
    setEmail(val);
    if (!val) { setEmailError(''); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) { setEmailError('Please enter a valid email.'); return; }
    const domain = val.split('@')[1]?.toLowerCase();
    if (domain && FREE_DOMAINS.includes(domain)) {
      setEmailError('Please use your company email address.');
    } else { setEmailError(''); }
  };

  const toggleLang = (lang: string) => {
    setLanguages(prev => prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]);
    if (lang !== 'Other') setOtherLanguage('');
  };

  const resolvedLanguages = languages.map(l => l === 'Other' ? (otherLanguage || 'Other') : l);
  const canSubmit = name && email && !emailError && company && languages.length > 0 && (!languages.includes('Other') || otherLanguage) && format;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
        subject: `Sample Token Pack Request — ${company} (${resolvedLanguages.join(', ')})`,
        from_name: 'Voqals Token Pack Request',
        replyto: email,
        Name: name,
        Email: email,
        Company: company,
        Languages: resolvedLanguages.join(', '),
        'Delivery Format': format,
        'Additional Notes': notes || '-',
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then(r => r.json());
      console.log('Token pack form response:', res);
      if (res.success) {
        setSubmitted(true);
        window.gtag?.('event', 'conversion', { send_to: 'AW-18066710209/65G5CK-OyJYcEMG98KZD', value: 1.0, currency: 'INR' });
      } else {
        console.error('Token pack form rejected:', res);
        alert('Submission failed: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Token pack form error:', err);
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OverlayShell isOpen={isOpen} onClose={onClose} title="Request a Sample Token Pack">
      {submitted ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center mb-6">
            <Check size={28} className="text-[#56CFFC]" />
          </div>
          <h2 className="text-white font-black text-[clamp(22px,2vw,36px)] tracking-tight uppercase mb-3">Request Received</h2>
          <p className="text-white/80 text-[clamp(12px,0.9vw,15px)] leading-relaxed max-w-sm mb-2">
            We'll reach out to schedule a brief intro call. Once we understand your project, we'll share a sample token pack in <span className="text-white font-semibold">{resolvedLanguages.join(', ')}</span> tailored to your use case.
          </p>
          <p className="text-white/60 text-[clamp(10px,0.75vw,12px)] mb-8">
            A confirmation has been sent to <span className="text-white/80">{email}</span>
          </p>
          <button type="button" onClick={onClose} className="px-8 py-3 rounded-full border border-white/10 text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white/5 transition-colors">
            Close
          </button>
        </div>
      ) : (
        <>
          <div className="flex-1 px-5 md:px-[clamp(28px,2.5vw,40px)] pt-3 md:pt-4 pb-3 flex flex-col gap-3 md:gap-3.5">

            {/* Name + Company + Email */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Your Name">
                <Input value={name} onChange={setName} placeholder="Jane Doe" required />
              </Field>
              <Field label="Company">
                <Input value={company} onChange={setCompany} placeholder="Acme AI" required />
              </Field>
              <Field label="Work Email">
                <Input value={email} onChange={validateEmail} placeholder="jane@company.com" type="email" error={!!emailError} required />
                {emailError && <p className="text-red-400 text-[10px] mt-0.5">{emailError}</p>}
              </Field>
            </div>

            {/* Languages — pills (multi-select) */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[9px] md:text-[clamp(8px,0.64vw,11px)] uppercase tracking-widest text-white/80 font-medium">
                Languages you need tokens for
              </label>
              <div className="flex flex-wrap gap-1.5">
                {PILL_LANGUAGES.map(lang => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => toggleLang(lang)}
                    className={`px-2.5 py-1 md:px-3 md:py-1.5 rounded-full text-[9px] md:text-[clamp(8px,0.6vw,11px)] font-bold tracking-[0.08em] uppercase transition-all duration-200 border ${
                      languages.includes(lang)
                        ? 'bg-[#56CFFC]/10 border-[#56CFFC]/50 text-[#56CFFC] shadow-[0_0_12px_rgba(86,207,252,0.15)]'
                        : 'bg-transparent border-white/10 text-gray-500 hover:text-white hover:border-white/25'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
              {languages.includes('Other') && (
                <input
                  type="text"
                  value={otherLanguage}
                  onChange={e => setOtherLanguage(e.target.value)}
                  placeholder="Type the language(s)..."
                  autoFocus
                  className="mt-1.5 w-full bg-white/[0.03] border border-white/10 rounded-xl py-2 px-3.5 text-white text-sm focus:outline-none focus:border-[#56CFFC]/50 transition-colors placeholder:text-gray-600"
                />
              )}
            </div>

            {/* Delivery Format — pills (single-select) */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[9px] md:text-[clamp(8px,0.64vw,11px)] uppercase tracking-widest text-white/80 font-medium">
                Preferred delivery format
              </label>
              <div className="flex flex-wrap gap-1.5">
                {DELIVERY_FORMATS.map(f => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFormat(prev => prev === f ? '' : f)}
                    className={`px-2.5 py-1 md:px-3 md:py-1.5 rounded-full text-[9px] md:text-[clamp(8px,0.6vw,11px)] font-bold tracking-[0.08em] uppercase transition-all duration-200 border ${
                      format === f
                        ? 'bg-[#56CFFC]/10 border-[#56CFFC]/50 text-[#56CFFC] shadow-[0_0_12px_rgba(86,207,252,0.15)]'
                        : 'bg-transparent border-white/10 text-gray-500 hover:text-white hover:border-white/25'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes — compact */}
            <Field label="Anything else?" optional>
              <input
                type="text"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g., specific use case (ASR / TTS), sample size, domain..."
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-2 px-3.5 text-white text-sm focus:outline-none focus:border-[#56CFFC]/50 transition-colors placeholder:text-gray-600"
              />
            </Field>

            {/* Info note */}
            <p className="text-[9px] md:text-[10px] text-white/60 leading-snug">
              Sample token packs are shared after a brief intro call to understand your project. We'll reach out to schedule one — no commitment required.
            </p>
          </div>

          {/* Footer — submit */}
          <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] py-3 md:py-4 border-t border-white/5 shrink-0">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="group relative w-full py-2.5 md:py-3 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full font-bold text-[10px] md:text-[11px] tracking-[0.2em] uppercase overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] disabled:opacity-40 disabled:hover:shadow-none flex items-center justify-center"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
              <span className="relative z-10 flex items-center gap-2">
                {submitting ? 'Submitting...' : 'Request Sample Pack'}
                {!submitting && <ArrowRight size={14} />}
              </span>
            </button>
          </div>
        </>
      )}
    </OverlayShell>
  );
}

/* ── Kept for backwards compat — no longer used ── */
export function DesktopProjectForm({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) { return null; }
export function MobileProjectForm({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) { return null; }
export function ProjectBriefForm({ onClose, compact }: { onClose: () => void; compact?: boolean }) { return null; }
export default function ProjectBriefDrawer({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) { return null; }

/* ═══ Helper Components ═══ */

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <span className="text-white/70">{label}</span>
      <span className="text-white truncate">{value}</span>
    </>
  );
}

function Field({ label, optional, children }: { label: string; optional?: boolean; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-[9px] md:text-[clamp(8px,0.64vw,11px)] uppercase tracking-widest text-white/80 font-medium">
        {label}
        {optional && <span className="text-white/40 ml-1 normal-case tracking-normal">(Optional)</span>}
      </label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = 'text', error, required }: {
  value: string; onChange: (v: string) => void; placeholder: string; type?: string; error?: string | boolean; required?: boolean;
}) {
  return (
    <div>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className={`w-full bg-white/[0.03] border border-white/10 rounded-xl py-2.5 px-4 text-white text-sm md:text-[clamp(12px,0.85vw,15px)] focus:outline-none transition-colors placeholder:text-gray-600 ${error ? 'border-red-500/50 focus:border-red-500' : 'focus:border-[#56CFFC]/50'}`}
      />
      {typeof error === 'string' && error && (
        <p className="text-red-400 text-[10px] md:text-[clamp(9px,0.6vw,11px)] mt-1 tracking-wide">{error}</p>
      )}
    </div>
  );
}

/* Checkbox-style radio — allows multi-select for goals */
function CheckRadio({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full px-4 py-3 md:py-3.5 rounded-xl text-[10px] md:text-[clamp(9px,0.69vw,12px)] font-bold tracking-[0.12em] uppercase transition-all duration-200 border text-left ${
        selected
          ? 'bg-[#56CFFC]/8 border-[#56CFFC]/40 text-[#56CFFC] shadow-[0_0_20px_rgba(86,207,252,0.1)]'
          : 'bg-white/[0.02] border-white/10 text-gray-400 hover:text-white hover:border-white/20'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
          selected ? 'bg-[#56CFFC]/20 border-[#56CFFC]/50' : 'border-white/20'
        }`}>
          {selected && <Check size={10} className="text-[#56CFFC]" />}
        </div>
        {children}
      </div>
    </button>
  );
}

/* Small pill for data-type selection */
function PillOption({ selected, onClick, children }: { key?: React.Key; selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 md:px-4 md:py-2 rounded-full text-[9px] md:text-[clamp(8px,0.64vw,11px)] font-bold tracking-[0.12em] uppercase transition-all duration-200 border ${
        selected
          ? 'bg-[#56CFFC]/10 border-[#56CFFC]/50 text-[#56CFFC]'
          : 'bg-transparent border-white/10 text-gray-500 hover:text-white hover:border-white/20'
      }`}
    >
      {children}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════
   Dataset Enquiry Overlay — "Enquire About Datasets"
   ═══════════════════════════════════════════════════════════ */
const DS_PURPOSES = ['Data acquisition', 'Licensing inquiries', 'Metadata schema request', 'General inquiry / Other'];

const DS_FREE_PROVIDERS = ['gmail', 'yahoo', 'hotmail', 'outlook', 'live', 'aol', 'icloud', 'mail', 'protonmail', 'proton', 'zoho', 'yandex', 'rediffmail', 'msn', 'googlemail'];

export function DatasetEnquiryOverlay({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [company, setCompany] = useState('');
  const [designation, setDesignation] = useState('');
  const [purpose, setPurpose] = useState('');
  const [message, setMessage] = useState('');
  const [attachPondFiles, setAttachPondFiles] = useState<any[]>([]);
  const [attachUploadedKey, setAttachUploadedKey] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Two-phase animation (same pattern as OverlayShell)
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  useEffect(() => { if (isOpen) setVisible(true); else setAnimateIn(false); }, [isOpen]);
  useEffect(() => { if (visible && isOpen) { const id = setTimeout(() => setAnimateIn(true), 20); return () => clearTimeout(id); } }, [visible, isOpen]);
  const handleTransitionEnd = useCallback(() => { if (!isOpen) setVisible(false); }, [isOpen]);
  useEffect(() => { if (isOpen) { const sc = document.getElementById('main-scroll-container'); if (sc) sc.style.overflow = 'hidden'; document.body.style.overflow = 'hidden'; return () => { if (sc) sc.style.overflow = ''; document.body.style.overflow = ''; }; } }, [isOpen]);

  useEffect(() => { if (isOpen) setSubmitted(false); }, [isOpen]);

  const validateEmail = (val: string) => {
    setEmail(val);
    if (!val) { setEmailError(''); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) { setEmailError('Please enter a valid email.'); return; }
    const domain = val.split('@')[1]?.toLowerCase();
    const provider = domain?.split('.')[0];
    if (provider && DS_FREE_PROVIDERS.includes(provider)) { setEmailError('Please use your work email.'); return; }
    setEmailError('');
  };

  const canSubmit = name && email && !emailError && company && purpose;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload: Record<string, string> = {
        access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
        subject: `Dataset Enquiry — ${company}`,
        from_name: 'Voqals Dataset Enquiry',
        replyto: email,
        Name: name,
        Email: email,
        Company: company,
        Designation: designation || '-',
        Purpose: purpose,
        Message: message || '-',
        'Form Type': 'Dataset Enquiry',
      };
      if (attachUploadedKey) payload.attachment = attachUploadedKey;
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then(r => r.json());
      if (res.success) {
        setSubmitted(true);
        window.gtag?.('event', 'conversion', { send_to: 'AW-18066710209/65G5CK-OyJYcEMG98KZD', value: 1.0, currency: 'INR' });
      } else {
        alert('Submission failed: ' + (res.message || 'Unknown error'));
      }
    } catch {
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex transition-opacity duration-300"
      style={{ opacity: animateIn ? 1 : 0, pointerEvents: animateIn ? 'auto' : 'none' }}
      onTransitionEnd={(e) => { if (e.propertyName === 'opacity' && !animateIn) handleTransitionEnd(); }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[12px] transition-opacity duration-300" style={{ opacity: animateIn ? 1 : 0 }} onClick={onClose} />
      <div
        className="relative z-10 ml-auto w-full md:w-[clamp(580px,52vw,900px)] h-full md:h-[calc(100vh-48px)] md:my-6 md:mr-[clamp(16px,1.5vw,28px)] md:rounded-[clamp(20px,1.85vw,32px)] flex flex-col overflow-hidden transition-transform duration-300 ease-out"
        style={{ background: 'linear-gradient(180deg, rgba(8,8,20,0.97) 0%, rgba(6,6,16,0.98) 100%)', transform: animateIn ? 'translateX(0)' : 'translateX(100%)' }}
      >
        {/* Gradient border */}
        <div className="hidden md:block absolute inset-0 rounded-[inherit] pointer-events-none z-10" style={{
          padding: '1.5px',
          background: 'linear-gradient(135deg, rgba(86,207,252,0.5) 0%, rgba(27,54,255,0.3) 50%, rgba(86,207,252,0.25) 100%)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
        }} />
        <div className="md:hidden absolute left-0 top-0 bottom-0 w-[1.5px] bg-gradient-to-b from-[#56CFFC]/40 via-[#1B36FF]/30 to-transparent" />
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[radial-gradient(circle,rgba(86,207,252,0.04)_0%,transparent_70%)] pointer-events-none" />

        {/* Header */}
        <div className="relative flex items-center justify-between px-5 md:px-[clamp(28px,2.5vw,40px)] pt-5 md:pt-[clamp(20px,2vw,28px)] pb-4 md:pb-[clamp(14px,1.2vw,20px)] border-b border-white/5 shrink-0">
          <button onClick={onClose} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full border border-white/10 group-hover:border-white/20 flex items-center justify-center transition-colors">
              <ChevronLeft size={16} />
            </div>
            <span className="text-[10px] md:text-[clamp(9px,0.69vw,11px)] font-bold uppercase tracking-widest">Close</span>
          </button>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 transition-colors text-gray-500 hover:text-white border border-white/5">
            <X size={14} />
          </button>
        </div>

        {submitted ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center mb-6">
              <Check size={28} className="text-[#56CFFC]" />
            </div>
            <h2 className="text-white font-black text-[clamp(22px,2vw,36px)] tracking-tight uppercase mb-3">Enquiry Received</h2>
            <p className="text-white/80 text-[clamp(12px,0.9vw,15px)] leading-relaxed max-w-sm mb-8">
              Thank you, {name.split(' ')[0]}. Our team will review your request and get back to you shortly.
            </p>
            <button type="button" onClick={onClose} className="px-8 py-3 rounded-full border border-white/10 text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white/5 transition-colors">
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Title */}
            <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] pt-5 md:pt-6 pb-2 shrink-0">
              <h1 className="text-white font-black text-[clamp(20px,1.8vw,32px)] tracking-tight uppercase">Dataset Enquiry</h1>
              <p className="text-gray-500 text-[clamp(10px,0.7vw,13px)] leading-relaxed mt-1">
                Interested in our production datasets? Fill this out and we'll get back to you.
              </p>
            </div>

            {/* Form */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 md:px-[clamp(28px,2.5vw,40px)] pt-4 md:pt-5 pb-4">
              <div className="flex flex-col gap-4 md:gap-5">
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <Field label="Name">
                    <Input value={name} onChange={setName} placeholder="Jane Doe" required />
                  </Field>
                  <Field label="Company">
                    <Input value={company} onChange={setCompany} placeholder="Acme AI" required />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <Field label="Designation" optional>
                    <Input value={designation} onChange={setDesignation} placeholder="ML Engineer" />
                  </Field>
                  <Field label="Work Email">
                    <Input value={email} onChange={validateEmail} placeholder="jane@company.com" type="email" error={emailError} required />
                  </Field>
                </div>

                <Field label="Purpose">
                  <StyledSelect value={purpose} onChange={setPurpose} options={DS_PURPOSES} placeholder="Select a purpose..." required />
                </Field>

                <Field label="Message" optional>
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Tell us about your project, timeline, or any specific requirements..."
                    rows={2}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-2.5 px-4 text-white text-sm md:text-[clamp(12px,0.85vw,15px)] placeholder:text-gray-600 focus:outline-none focus:border-[#56CFFC]/50 transition-colors resize-none"
                  />
                </Field>

                <Field label="Attachment" optional>
                  <div className="filepond-dark">
                    <FilePond
                      files={attachPondFiles}
                      onupdatefiles={setAttachPondFiles}
                      onprocessfile={(_error, file) => { if (file.serverId) setAttachUploadedKey(file.serverId as string); }}
                      onremovefile={() => setAttachUploadedKey('')}
                      allowMultiple={false}
                      maxFiles={1}
                      maxFileSize="50MB"
                      name="attachment"
                      server={filepondServer}
                      labelIdle='Drag & drop or <span class="filepond--label-action">Browse</span> — up to 50 MB'
                      credits={false}
                    />
                  </div>
                </Field>
              </div>
            </div>

            {/* Submit */}
            <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] py-3 md:py-4 border-t border-white/5 shrink-0">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className="w-full py-3 md:py-[clamp(12px,1vw,16px)] rounded-full font-bold text-[10px] md:text-[clamp(9px,0.7vw,12px)] tracking-[0.2em] uppercase transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white shadow-[0_0_20px_rgba(27,54,255,0.3)] hover:scale-[1.02]"
              >
                {submitting ? 'Submitting...' : 'Submit Enquiry'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function StyledSelect({ value, onChange, options, placeholder, required }: { value: string; onChange: (v: string) => void; options: string[]; placeholder: string; required?: boolean }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        aria-label={placeholder}
        className="w-full appearance-none bg-white/[0.03] border border-white/10 rounded-xl py-2.5 px-4 pr-8 text-white text-sm md:text-[clamp(12px,0.85vw,15px)] focus:outline-none focus:border-[#56CFFC]/50 transition-colors"
      >
        <option value="" className="bg-[#0a0a14]">{placeholder}</option>
        {options.map(o => <option key={o} value={o} className="bg-[#0a0a14]">{o}</option>)}
      </select>
      <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Use Case Brief — lightweight lead-capture overlay opened from
   the homepage Use Cases section. Pre-fills the use case the
   buyer clicked, asks for the bare minimum (name, email,
   company, optional message), and submits via Web3Forms.
   Much simpler than the full ProjectBriefOverlay.
   ═══════════════════════════════════════════════════════════ */

const USE_CASE_OPTIONS = ['Conversational AI', 'Voice Cloning', 'Generative Music', 'Speech Recognition', 'Other'];
const UC_FREE_PROVIDERS = ['gmail', 'yahoo', 'hotmail', 'outlook', 'live', 'aol', 'icloud', 'mail', 'protonmail', 'proton', 'zoho', 'yandex', 'rediffmail', 'msn', 'googlemail'];

export function UseCaseBriefOverlay({
  isOpen, onClose, useCase: initialUseCase = '',
}: { isOpen: boolean; onClose: () => void; useCase?: string }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [company, setCompany] = useState('');
  const [useCase, setUseCase] = useState(initialUseCase);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Two-phase animation (matches DatasetEnquiryOverlay pattern)
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  useEffect(() => { if (isOpen) setVisible(true); else setAnimateIn(false); }, [isOpen]);
  useEffect(() => { if (visible && isOpen) { const id = setTimeout(() => setAnimateIn(true), 20); return () => clearTimeout(id); } }, [visible, isOpen]);
  const handleTransitionEnd = useCallback(() => { if (!isOpen) setVisible(false); }, [isOpen]);
  useEffect(() => { if (isOpen) { const sc = document.getElementById('main-scroll-container'); if (sc) sc.style.overflow = 'hidden'; document.body.style.overflow = 'hidden'; return () => { if (sc) sc.style.overflow = ''; document.body.style.overflow = ''; }; } }, [isOpen]);

  // Re-sync the pre-selected use case when the overlay (re)opens with a different one.
  useEffect(() => {
    if (isOpen) {
      setUseCase(initialUseCase);
      setSubmitted(false);
    }
  }, [isOpen, initialUseCase]);

  const validateEmail = (val: string) => {
    setEmail(val);
    if (!val) { setEmailError(''); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) { setEmailError('Please enter a valid email.'); return; }
    const domain = val.split('@')[1]?.toLowerCase();
    const provider = domain?.split('.')[0];
    if (provider && UC_FREE_PROVIDERS.includes(provider)) { setEmailError('Please use your work email.'); return; }
    setEmailError('');
  };

  const canSubmit = name && email && !emailError && company && useCase;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae',
          subject: `Use Case Brief — ${useCase} — ${company}`,
          from_name: 'Voqals Use Case Brief',
          replyto: email,
          Name: name,
          Email: email,
          Company: company,
          'Use Case': useCase,
          Message: message || '-',
          'Form Type': 'Use Case Brief (Homepage)',
        }),
      }).then(r => r.json());
      if (res.success) {
        setSubmitted(true);
        window.gtag?.('event', 'conversion', { send_to: 'AW-18066710209/65G5CK-OyJYcEMG98KZD', value: 1.0, currency: 'INR' });
      } else {
        alert('Submission failed: ' + (res.message || 'Unknown error'));
      }
    } catch {
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex transition-opacity duration-300"
      style={{ opacity: animateIn ? 1 : 0, pointerEvents: animateIn ? 'auto' : 'none' }}
      onTransitionEnd={(e) => { if (e.propertyName === 'opacity' && !animateIn) handleTransitionEnd(); }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[12px] transition-opacity duration-300" style={{ opacity: animateIn ? 1 : 0 }} onClick={onClose} />
      <div
        className="relative z-10 ml-auto w-full md:w-[clamp(520px,46vw,780px)] h-full md:h-[calc(100vh-48px)] md:my-6 md:mr-[clamp(16px,1.5vw,28px)] md:rounded-[clamp(20px,1.85vw,32px)] flex flex-col overflow-hidden transition-transform duration-300 ease-out"
        style={{ background: 'linear-gradient(180deg, rgba(8,8,20,0.97) 0%, rgba(6,6,16,0.98) 100%)', transform: animateIn ? 'translateX(0)' : 'translateX(100%)' }}
      >
        {/* Gradient border */}
        <div className="hidden md:block absolute inset-0 rounded-[inherit] pointer-events-none z-10" style={{
          padding: '1.5px',
          background: 'linear-gradient(135deg, rgba(86,207,252,0.5) 0%, rgba(27,54,255,0.3) 50%, rgba(86,207,252,0.25) 100%)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
        }} />
        <div className="md:hidden absolute left-0 top-0 bottom-0 w-[1.5px] bg-gradient-to-b from-[#56CFFC]/40 via-[#1B36FF]/30 to-transparent" />
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[radial-gradient(circle,rgba(86,207,252,0.04)_0%,transparent_70%)] pointer-events-none" />

        {/* Header */}
        <div className="relative flex items-center justify-between px-5 md:px-[clamp(28px,2.5vw,40px)] pt-5 md:pt-[clamp(20px,2vw,28px)] pb-4 md:pb-[clamp(14px,1.2vw,20px)] border-b border-white/5 shrink-0">
          <button onClick={onClose} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full border border-white/10 group-hover:border-white/20 flex items-center justify-center transition-colors">
              <ChevronLeft size={16} />
            </div>
            <span className="text-[10px] md:text-[clamp(9px,0.69vw,11px)] font-bold uppercase tracking-widest">Close</span>
          </button>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 transition-colors text-gray-500 hover:text-white border border-white/5">
            <X size={14} />
          </button>
        </div>

        {submitted ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center mb-6">
              <Check size={28} className="text-[#56CFFC]" />
            </div>
            <h2 className="text-white font-black text-[clamp(22px,2vw,36px)] tracking-tight uppercase mb-3">Brief Received</h2>
            <p className="text-white/80 text-[clamp(12px,0.9vw,15px)] leading-relaxed max-w-sm mb-8">
              Thank you, {name.split(' ')[0]}. Our team will review your {useCase || 'use case'} brief and reach out within 24 hours.
            </p>
            <button type="button" onClick={onClose} className="px-8 py-3 rounded-full border border-white/10 text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white/5 transition-colors">
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Title */}
            <div className="px-5 md:px-[clamp(28px,2.5vw,40px)] pt-5 md:pt-6 pb-2 shrink-0">
              <h1 className="text-white font-black text-[clamp(20px,1.8vw,32px)] tracking-tight uppercase">Brief Us</h1>
              <p className="text-gray-500 text-[clamp(10px,0.7vw,13px)] leading-relaxed mt-1">
                Tell us what you're building and we'll come back with a scope in 48 hours.
              </p>
            </div>

            {/* Form */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 md:px-[clamp(28px,2.5vw,40px)] pt-4 md:pt-5 pb-4">
              <div className="flex flex-col gap-4 md:gap-5">
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <Field label="Name">
                    <Input value={name} onChange={setName} placeholder="Jane Doe" required />
                  </Field>
                  <Field label="Work Email">
                    <Input value={email} onChange={validateEmail} placeholder="jane@company.com" type="email" error={emailError} required />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <Field label="Company">
                    <Input value={company} onChange={setCompany} placeholder="Acme AI" required />
                  </Field>
                  <Field label="Use Case">
                    <StyledSelect value={useCase} onChange={setUseCase} options={USE_CASE_OPTIONS} placeholder="Select a use case…" required />
                  </Field>
                </div>

                <Field label="What you're building" optional>
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="A few sentences on your model, languages, hours needed, timeline…"
                    rows={3}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-2.5 px-4 text-white text-sm md:text-[clamp(12px,0.85vw,15px)] placeholder:text-gray-600 focus:outline-none focus:border-[#56CFFC]/50 transition-colors resize-none"
                  />
                </Field>
              </div>
            </div>

            {/* Submit bar */}
            <div className="border-t border-white/5 px-5 md:px-[clamp(28px,2.5vw,40px)] py-4 md:py-[clamp(14px,1.2vw,20px)] shrink-0">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className="w-full px-6 py-3 md:py-3.5 rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white text-[11px] md:text-[clamp(10px,0.75vw,13px)] font-bold tracking-[0.2em] uppercase transition-all hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(27,54,255,0.4)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none flex items-center justify-center gap-3"
              >
                {submitting ? 'Sending…' : 'Send Brief'}
                {!submitting && <ArrowRight size={14} />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
